import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { useCreatePart } from "@/hooks/useParts";
import { useToast } from "@/hooks/use-toast";
import { useAllZonesFlat, useUpsertPartZoneStock } from "@/hooks/useFacilityZones";
import { useAssets } from "@/hooks/useAssets";
import { useVendorContacts } from "@/hooks/useVendorContacts";
import { VendorSearchInput } from "@/components/shared/VendorSearchInput";
import { LocalImageDropZone } from "@/components/shared/LocalImageDropZone";
import { useUploadEntityImage } from "@/hooks/useEntityImages";
import { LocalFileDropZone } from "@/components/shared/LocalFileDropZone";
import { useUploadEntityFile } from "@/hooks/useEntityFiles";
import { supabase } from "@/integrations/supabase/client";

export function CreatePartDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [partNumber, setPartNumber] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [qtyInStock, setQtyInStock] = useState("");
  const [minQuantity, setMinQuantity] = useState("");
  const [reorderQuantity, setReorderQuantity] = useState("");
  const [unit, setUnit] = useState("each");
  const [locationId, setLocationId] = useState("");
  const [initialQty, setInitialQty] = useState("");
  const [notes, setNotes] = useState("");
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [assetId, setAssetId] = useState("");
  const [assetSearch, setAssetSearch] = useState("");
  const [assetDropdownOpen, setAssetDropdownOpen] = useState(false);
  const assetInputRef = useRef<HTMLInputElement>(null);
  const [vendorId, setVendorId] = useState("");

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const createPart = useCreatePart();
  const upsertZoneStock = useUpsertPartZoneStock();
  const uploadImage = useUploadEntityImage("part");
  const uploadFile = useUploadEntityFile("part");
  const zones = useAllZonesFlat();
  const { data: assets = [] } = useAssets();
  const { data: vendors = [] } = useVendorContacts();

  const selectedAsset = assets.find((a) => a.id === assetId);
  const filteredAssets = assets.filter((a) =>
    a.name.toLowerCase().includes(assetSearch.toLowerCase())
  );
  const selectedVendor = vendors.find((v) => v.id === vendorId);

  const resetForm = () => {
    setName(""); setDescription(""); setPartNumber(""); setUnitCost("");
    setQtyInStock(""); setMinQuantity(""); setReorderQuantity(""); setUnit("each"); setLocationId(""); setInitialQty(""); setNotes("");
    setPendingImages([]);
    setPendingFiles([]);
    setAssetId(""); setAssetSearch(""); setAssetDropdownOpen(false);
    setVendorId("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createPart.mutate(
      {
        name,
        description: description || undefined,
        part_number: partNumber || undefined,
        unit_cost: unitCost ? parseFloat(unitCost) : undefined,
        qty_in_stock: qtyInStock ? parseFloat(qtyInStock) : 0,
        min_quantity: minQuantity ? parseFloat(minQuantity) : 0,
        reorder_quantity: reorderQuantity ? parseFloat(reorderQuantity) : undefined,
        unit: unit || "each",
        vendor_id: vendorId || undefined,
        notes: notes || undefined,
      },
      {
        onSuccess: async (newPart) => {
          // Seed initial zone stock if provided
          if (locationId && initialQty) {
            upsertZoneStock.mutate({
              part_id: newPart.id,
              zone_id: locationId,
              quantity: parseFloat(initialQty) || 0,
              org_id: newPart.org_id,
            });
          }
          if (assetId) {
            const { error: assetLinkError } = await supabase.from("part_asset" as any).insert({ part_id: newPart.id, asset_id: assetId });
            if (assetLinkError) {
              console.error("Failed to link asset:", assetLinkError);
              toast({ title: "Asset link failed", description: assetLinkError.message, variant: "destructive" });
            } else {
              queryClient.invalidateQueries({ queryKey: ["part-assets", newPart.id] });
            }
          }
          pendingImages.forEach((file) =>
            uploadImage.mutate({ file, entityId: newPart.id, orgId: newPart.org_id })
          );
          pendingFiles.forEach((file) =>
            uploadFile.mutate({ file, entityId: newPart.id, orgId: newPart.org_id })
          );
          resetForm();
          setOpen(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="h-4 w-4 mr-2" />New Part</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Part</DialogTitle>
          <DialogDescription>Add a part to inventory.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., O-Ring 2 inch" required />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>OEM Part Number</Label>
              <Input value={partNumber} onChange={(e) => setPartNumber(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Unit Cost ($)</Label>
              <Input type="number" step="0.01" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Qty In Stock</Label>
              <Input type="number" value={qtyInStock} onChange={(e) => setQtyInStock(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Unit</Label>
              <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="each" />
            </div>
            <div className="space-y-2">
              <Label>Min Quantity</Label>
              <Input type="number" value={minQuantity} onChange={(e) => setMinQuantity(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Reorder Qty</Label>
              <Input type="number" placeholder="e.g. 25" value={reorderQuantity} onChange={(e) => setReorderQuantity(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Initial Location <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger><SelectValue placeholder="Select location..." /></SelectTrigger>
                <SelectContent>
                  {zones.map((z) => (
                    <SelectItem key={z.id} value={z.id}>{z.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Qty at Location</Label>
              <Input
                type="number"
                value={initialQty}
                onChange={(e) => setInitialQty(e.target.value)}
                placeholder="0"
                disabled={!locationId}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Primary Vendor <span className="text-muted-foreground font-normal">(optional)</span></Label>
            {selectedVendor ? (
              <div className="flex items-center gap-2 h-9 px-3 rounded-md border bg-muted/40 text-sm">
                <span className="flex-1 truncate">{selectedVendor.company}</span>
                <button type="button" onClick={() => setVendorId("")} className="text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <VendorSearchInput vendors={vendors} onSelect={(v) => setVendorId(v.id)} />
            )}
          </div>
          <div className="space-y-2">
            <Label>Linked Asset <span className="text-muted-foreground font-normal">(optional)</span></Label>
            {selectedAsset ? (
              <div className="flex items-center gap-2 h-9 px-3 rounded-md border bg-muted/40 text-sm">
                <span className="flex-1 truncate">{selectedAsset.name}</span>
                <button type="button" onClick={() => { setAssetId(""); setAssetSearch(""); }} className="text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  ref={assetInputRef}
                  placeholder="Search assets..."
                  value={assetSearch}
                  onChange={(e) => { setAssetSearch(e.target.value); setAssetDropdownOpen(true); }}
                  onFocus={() => setAssetDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setAssetDropdownOpen(false), 150)}
                />
                {assetDropdownOpen && filteredAssets.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover shadow-md">
                    {filteredAssets.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                        onMouseDown={() => { setAssetId(a.id); setAssetSearch(""); setAssetDropdownOpen(false); }}
                      >
                        {a.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <div className="space-y-2">
            <Label>Photos</Label>
            <LocalImageDropZone files={pendingImages} onChange={setPendingImages} />
          </div>
          <div className="space-y-2">
            <Label>Files & Manuals</Label>
            <LocalFileDropZone files={pendingFiles} onChange={setPendingFiles} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={createPart.isPending}>
              {createPart.isPending ? "Creating..." : "Create Part"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
